import React, { useMemo } from 'react';
import { t } from 'i18next';
import { func, bool, number, object, arrayOf, oneOf } from 'prop-types';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import ProductDetail from '../ProductDetail';

import { shoppingCartSelector } from '~screens/Dashboard/screens/Home/redux/selectors';
import Timer from '~components/MenuItem/components/Timer';
import { itemPropTypes, upsellingVariantPropTypes } from '~propTypes/shoppingCart';
import { productTypes } from '~constants/products';
import { ANALYTICS_CLASSES } from '~constants/analytics';
import { SIZES } from '~screens/Dashboard/screens/Home/constants';
import { itemHasRulesAdded } from '~utils/shoppingCart';
import CustomButton from '~components/CustomButton';
import {
  CART_VARIANT_SIZES,
  KEY_EDIT_PIZZA,
  MAKE_YOUR_PIZZA
} from '~components/CartContent/components/CartVariant/constants';
import BenefitWarningBudget from '~components/BenefitWarningBudget';
import { formatPrice } from '~utils/number';
import UpsellingOption from '~components/CartContent/components/UpsellingOption';
import { isEmptyObject } from '~utils/object';
import { InfoBadgePromo } from '~components/CartContent/components/PromoSelling/Components/InfoBadgePromo';

import styles from './styles.module.scss';
import { getVariantTotal, canAddWithPoints, hasDiscount, itemIsNotFree, itemLoyaltyBenefit } from './utils';

// eslint-disable-next-line complexity
function CartVariant({
  item,
  onAddVariant,
  onRemoveVariant,
  cartLoading,
  canAddPoints,
  currentPoints,
  onModifyPizza,
  upsellingVariant,
  index,
  showUpselling,
  canModifyItem,
  isCheckout,
  combos,
  size = CART_VARIANT_SIZES.LARGE
}) {
  const { ingredients = [] } = item;
  const loyaltyExperienceEnabled = useSelector(state => state.home.coreSettings?.loyaltyExperienceEnabled);

  const pizzaCustomizable =
    !item.offerType && item?.name?.toLowerCase() !== MAKE_YOUR_PIZZA ? item.customizable : true;

  const canModify =
    (item.menuableType === productTypes.PIZZA_VARIANT || item.offerType) &&
    item.size !== SIZES.INDIVIDUAL &&
    !ingredients.some(ingredient => ingredient.freeIngredient) &&
    pizzaCustomizable;

  const shoppingCart = useSelector(shoppingCartSelector);
  const appliedBenefits = shoppingCart?.appliedBenefits;
  const loyaltyLevels = useSelector(state => state?.papaPoints?.loyaltyLevels);
  const loyaltyDiscount = useMemo(
    () => itemLoyaltyBenefit(appliedBenefits, item, loyaltyLevels),
    [loyaltyLevels, item, appliedBenefits]
  );

  const addItemDisabled =
    canAddPoints ||
    canAddWithPoints(item.pointsSpent, currentPoints, item.quantity) ||
    itemHasRulesAdded(item);

  const comboInfo = combos.find(({ id }) => item.offerId === id);

  const modifyQuantity = (
    <div className={styles.buttonsContainer}>
      {canModifyItem && (
        // Todo Refactor Button and remove Font awesome
        <button
          type="button"
          className={cn(
            'row center middle subtitle-medium',
            styles.amountButton,
            ANALYTICS_CLASSES.cart.btnDecreaseProductQuantity,
            {
              [styles.disabled]: cartLoading || item.quantity <= 0
            }
          )}
          disabled={cartLoading || item.quantity <= 0}
          aria-label={t('CartVariant:btnDecreaseProductQuantity')}
          onClick={onRemoveVariant}>
          <i className="icon-minus text-select full-width" />
        </button>
      )}
      <span className={cn(styles.text, styles.textQuantity)}>{item.quantity}</span>
      {canModifyItem && (
        // Todo Refactor Button and remove Font awesome
        <button
          type="button"
          className={cn(
            'row center middle subtitle-medium',
            styles.amountButton,
            ANALYTICS_CLASSES.cart.btnIncreaseProductQuantity,
            {
              [styles.disabled]: cartLoading || addItemDisabled
            }
          )}
          onClick={onAddVariant}
          aria-label={t('CartVariant:btnIncreaseProductQuantity')}>
          <i className="icon-plus text-select full-width" />
        </button>
      )}
    </div>
  );
  const price = (
    <span
      className={cn(`subtitle-small ${styles.variantTotal}`, {
        'text-caribbean-green': item.pointsSpent
      })}>
      {itemIsNotFree(item) && hasDiscount(item) && loyaltyExperienceEnabled && (
        <span className={styles.crossOutText}>{formatPrice(item?.subtotal)}</span>
      )}
      {itemIsNotFree(item) || item.showPriceZero ? getVariantTotal(item) : t('MenuItem:freeItem')}
    </span>
  );

  if (size === CART_VARIANT_SIZES.SMALL) {
    return (
      <div className="row space-between">
        {modifyQuantity}
        {price}
      </div>
    );
  }

  return (
    <div className="m-bottom-3">
      {isCheckout && loyaltyExperienceEnabled && hasDiscount(item) && loyaltyDiscount?.discount && (
        <BenefitWarningBudget
          text={
            item?.quantity > 1
              ? t('MenuItem:discount', { discount: loyaltyDiscount?.discount || 0 }) +
                t('MenuItem:onlyFirstProduct')
              : t(`MenuItem:${item?.size ? 'discountInSize' : 'discount'}`, {
                  discount: loyaltyDiscount?.discount || 0,
                  size: t(`MenuItem:${item?.size}`)
                })
          }
          className={styles.benefitContainer}
        />
      )}
      <div className="m-bottom-2">
        {comboInfo?.countdownTimer && <Timer endDatetime={comboInfo.endDatetime} className={styles.timer} />}
      </div>
      <div className={styles.header}>
        <span className={styles.text}>{item.name}</span>
        {modifyQuantity}
      </div>
      <ProductDetail item={item} />
      {showUpselling && !isEmptyObject(upsellingVariant) && (
        <UpsellingOption
          productId={item?.offerId || item?.menuableId}
          quantity={item?.quantity}
          upsellingVariant={upsellingVariant}
          index={index}
        />
      )}
      <div className="row space-between middle m-bottom-3">
        {canModify && canModifyItem && (
          <CustomButton
            id={`${KEY_EDIT_PIZZA}-${item.menuableId}-${item.name.trim().replace(/ /g, '_').toLowerCase()}`}
            type="button"
            onClick={onModifyPizza}
            text={t('CartVariant:personalize')}
            btnClassType="link"
            className={cn(ANALYTICS_CLASSES.cart.btnModifyProduct)}
            textCapitalize
          />
        )}
        {item && <InfoBadgePromo cartItem={item} />}
        {price}
      </div>
    </div>
  );
}

CartVariant.propTypes = {
  item: itemPropTypes.isRequired,
  onAddVariant: func.isRequired,
  onModifyPizza: func.isRequired,
  onRemoveVariant: func.isRequired,
  canAddPoints: bool,
  canModifyItem: bool,
  cartLoading: bool,
  combos: arrayOf(object),
  currentPoints: number,
  index: number,
  isCheckout: bool,
  showUpselling: bool,
  size: oneOf(Object.values(CART_VARIANT_SIZES)),
  upsellingVariant: upsellingVariantPropTypes
};

CartVariant.defaultProps = {
  upsellingVariant: {}
};

export default CartVariant;
